import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Clock, Shield, Scissors, Users, Dumbbell, CalendarCheck, TrendingUp, HeartHandshake } from 'lucide-react';

const factsData = {
  de: {
    sectionTitle: 'DHI auf einen Blick',
    sectionSubtitle: 'Die wichtigsten Fakten zu Ihrer Behandlung',
    facts: [
      { icon: Clock, value: '6–8 Std.', label: 'OP-Dauer', detail: 'Unter lokaler Betäubung' },
      { icon: Shield, value: 'Lokal', label: 'Betäubung', detail: 'Schmerzfrei & sicher' },
      { icon: Scissors, value: 'Ohne Rasur', label: 'Oberkopf / Deckhaar', detail: 'Bestehendes Haar bleibt' },
      { icon: Users, value: '12 Tage', label: 'Gesellschaftsfähig', detail: 'Ohne sichtbare Krusten' },
      { icon: Dumbbell, value: '2 Wochen', label: 'Sport möglich', detail: 'Leichte Belastung vorher ok' },
      { icon: CalendarCheck, value: '12–18 Mon.', label: 'Endergebnis', detail: 'Volles, natürliches Haar' },
      { icon: TrendingUp, value: '95%+', label: 'Anwachsrate', detail: 'Klinisch bestätigt' },
      { icon: HeartHandshake, value: '12 Monate', label: 'Nachsorge', detail: 'Inklusive & persönlich' },
    ],
  },
  en: {
    sectionTitle: 'DHI at a Glance',
    sectionSubtitle: 'Key facts about your treatment',
    facts: [
      { icon: Clock, value: '6–8 hrs', label: 'Procedure time', detail: 'Under local anesthesia' },
      { icon: Shield, value: 'Local', label: 'Anesthesia', detail: 'Pain-free & safe' },
      { icon: Scissors, value: 'No shaving', label: 'Scalp / existing hair', detail: 'Existing hair stays' },
      { icon: Users, value: '12 days', label: 'Socially presentable', detail: 'No visible crusts' },
      { icon: Dumbbell, value: '2 weeks', label: 'Sports possible', detail: 'Light activity ok before' },
      { icon: CalendarCheck, value: '12–18 mo.', label: 'Final result', detail: 'Full, natural hair' },
      { icon: TrendingUp, value: '95%+', label: 'Growth rate', detail: 'Clinically confirmed' },
      { icon: HeartHandshake, value: '12 months', label: 'Aftercare', detail: 'Included & personal' },
    ],
  },
};

const DhiQuickFacts = () => {
  const { features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();

  if (!features?.showDhiQuickFacts) return null;

  const t = factsData[language] || factsData.de;

  return (
    <section
      className={`py-14 md:py-20 relative overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0a0a0a]' : 'bg-gradient-to-b from-gray-50 to-white'
      }`}
      data-testid="dhi-quick-facts-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t.sectionTitle}
          </h2>
          <p className={`text-sm md:text-base max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.sectionSubtitle}
          </p>
        </div>

        {/* Compact Facts Strip */}
        <div className={`rounded-2xl border overflow-hidden ${
          isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white'
        }`}>
          <div className="grid grid-cols-2 md:grid-cols-4">
            {t.facts.map((fact, idx) => {
              const Icon = fact.icon;
              const isLast = idx === t.facts.length - 1;
              const isEvenRow = idx >= 4;
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-3 px-4 py-3.5 md:px-5 md:py-4 ${
                    isDark
                      ? `${idx % 2 !== 0 ? 'bg-white/[0.01]' : ''} ${idx >= 4 ? 'border-t border-white/5' : ''} ${idx % 4 !== 0 ? 'md:border-l md:border-white/5' : ''}`
                      : `${idx % 2 !== 0 ? 'bg-gray-50/50' : ''} ${idx >= 4 ? 'border-t border-gray-100' : ''} ${idx % 4 !== 0 ? 'md:border-l md:border-gray-100' : ''}`
                  } ${idx >= 2 && idx < 4 ? isDark ? 'border-t border-white/5 md:border-t-0' : 'border-t border-gray-100 md:border-t-0' : ''}`}
                  data-testid={`quick-fact-${idx}`}
                >
                  <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                    isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm md:text-base font-bold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {fact.value}
                    </div>
                    <div className={`text-[11px] md:text-xs leading-tight ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {fact.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DhiQuickFacts;
